import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import Article from '../../../smoothr-web-app-core/models/Article';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { OrderType } from '../../../smoothr-web-app-core/enums/OrderType';
import ArticleGroup from '../../../smoothr-web-app-core/models/ArticleGroup';
import { OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import { environment } from 'src/environments/environment';
import {
	defaultsToArticleOption,
	getBasePrice,
	getPrice,
	numberD,
	numberToCurrency,
} from '../../../smoothr-web-app-core/utils/utils';
import { ValidationUtils } from 'src/smoothr-web-app-core/utils/validation-utils';
import OptionGroup from 'src/smoothr-web-app-core/models/OptionGroup';

@Component({
	selector: 'app-item-view',
	templateUrl: './item-view.component.html',
	styleUrls: ['item-view.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ItemViewComponent {
	@Input() currency: string;
	@Input() hasOrder = false;
	@Input()
	set article(value: Article) {
		this._article = value;
		// this.hasImg = !!value.assets.medium;
		this.reloadPrice();
	}
	@Input()
	set preorderType(value: PreorderType) {
		this._preorderType = value;
		this.reloadPrice();

		if (this.article?.groups?.length > 0 && this.article.groups.filter(it => it.visible)?.length > 0) {
			this.ifArticleHaveGroups = true;
		}
	}

	@Output() addToBasketEvent = new EventEmitter();
	@Output() regularClickEvent = new EventEmitter();

	price: number;
	ot = OrderType;
	environment = environment;
	priceText = '';
	numberToCurrency = numberToCurrency;
	getBasePrice = getBasePrice;
	hasImg = true;
	ifArticleHaveGroups: boolean = false;

	private _article: Article;
	private _preorderType: PreorderType = null;

	constructor() {}

	get article(): Article {
		return this._article;
	}
	get preorderType(): PreorderType {
		return this._preorderType;
	}

	private reloadPrice() {
		const articleGroup = new ArticleGroup();
		articleGroup.article = this.article;
		articleGroup.groups = defaultsToArticleOption(this.article, [], [], this.preorderType);

		articleGroup.quantity = 1;

		const article = articleGroup.article;
		let price = numberD(article.price);
		const processedGroups: string[] = [];
		article.groups.forEach(grp => {
			if (processedGroups.indexOf(grp._id) >= 0) return;
			if (ValidationUtils.isGroupDependencyFulfilled(articleGroup.article, articleGroup.groups, grp).times < 0) return;
			if (this.hasExcludePfandTag(article) && this.hasIsPfandTag(grp)) {
				return;
			}
			if (grp.requiredAmount == grp.limit) {
				const minimumItem = this.minOfItem(
					grp.articles.filter(it => it.visible),
					opt => numberD(opt.price)
				);

				price += numberD(minimumItem.price) * grp.requiredAmount;
				const dependants = this.dependenciesRegardingOptionArticle(article, article.groups, minimumItem);
				dependants.forEach(dependant => {
					const dependantMinimumItem = this.minOfItem(
						dependant.articles.filter(it => it.visible),
						opt => numberD(opt.price)
					);
					price += grp.requiredAmount * dependant.requiredAmount * numberD(dependantMinimumItem.price);
					processedGroups.push(dependant._id);
				});
				processedGroups.push(grp._id);
			}
		});
		const tagPrice = this.findTagPrice(this.article);
		if (tagPrice && tagPrice > 0) {
			price = price - tagPrice;
		}
		this.price = price;
		this.priceText = numberToCurrency(this.price, this.currency);
		return;
	}

	private minOfItem<T>(items: T[], transformer: (item: T) => number): T {
		if (items.length === 1) {
			return items[0];
		}
		const values = items.map(item => transformer(item));
		const min = Math.min(...values);
		const index = values.indexOf(min);
		return items[index];
	}

	dependenciesRegardingOptionArticle(article: Article, groups: OptionGroup[], optionArticle: Article): OptionGroup[] {
		return article.groupDependencies
			.map(gd => {
				if (gd.dependencies.find(dependeci => dependeci.groupArticles.includes(optionArticle._id)) == null) {
					return null;
				} else {
					return groups.find(it => gd.group === it._id);
				}
			})
			.filter(it => !!it);
	}

	isFromPrice(article: Article) {
		return !!article.tags.find(tag => tag.identifier === 'fromPrice');
	}

	getArticlePrice(article: Article, price: number) {
		let totalPrice = price;
		const tagPrice = this.findTagPrice(article);
		if (tagPrice && tagPrice > 0) {
			totalPrice = totalPrice - tagPrice;
		}
		return totalPrice;
	}
	findTagPrice(article: Article) {
		if (article && article.tags && article.tags.length > 0) {
			const foundTag = article?.tags?.find(
				it => it?.identifier === 'deposit_25' || it?.identifier === 'deposit_15' || it?.identifier === 'deposit_8'
			);
			if (foundTag) {
				return foundTag.identifier === 'deposit_25'
					? 0.25
					: foundTag.identifier === 'deposit_15'
						? 0.15
						: foundTag.identifier === 'deposit_8'
							? 0.8
							: 0;
			}
		}
		return null;
	}

	checkPfandTag(article: Article) {
		if (article.tags?.length === 0) {
			return '';
		}
		const deposit25 = article.tags.find(it => it.identifier === 'deposit_25') ? 'deposit25' : '';
		const deposit15 = article.tags.find(it => it.identifier === 'deposit_15') ? 'deposit15' : '';
		const deposit8 = article.tags.find(it => it.identifier === 'deposit_8') ? 'deposit8' : '';

		return deposit15 || deposit25 || deposit8;
	}

	showMeasurement(article: Article) {
		if (article) {
			if (article?.measurement?.refAmount && article?.measurement?.refUnit) {
				if (article?.measurement?.refAmount == 100 && article?.measurement?.refUnit === 'g') {
					return (
						'1' +
						' ' +
						'kg' +
						' = ' +
						numberToCurrency(getBasePrice(article, OrderType.PREORDER, this.preorderType) * 10, this.currency)
					);
				}
				if (article?.measurement?.refAmount == 100 && article?.measurement?.refUnit === 'ml') {
					return (
						'1' +
						' ' +
						'l' +
						' = ' +
						numberToCurrency(getBasePrice(article, OrderType.PREORDER, this.preorderType) * 10, this.currency)
					);
				}
				return (
					article.measurement.refAmount +
					' ' +
					article.measurement.refUnit +
					' = ' +
					numberToCurrency(getBasePrice(article, OrderType.PREORDER, this.preorderType), this.currency)
				);
			}
		}

		return '';
	}

	addToBasket(event: Event) {
		event.preventDefault();
		event.stopPropagation();
		this.addToBasketEvent.emit();
	}

	hasExcludePfandTag(article: Article) {
		return article.tags?.some(it => it.identifier === 'exclude_pfand');
	}
	hasIsPfandTag(group: OptionGroup) {
		return group.tags?.some(it => it.identifier === 'is_pfand');
	}
}
